// KeyboardAccessoryBar — VIN-2026-83
//
// A theme-aware "Done" toolbar that sits directly above the iOS soft keyboard.
//
// Why this exists: iOS's native input-accessory bar (the ‹ › / Done toolbar)
// can't be reliably tinted from JS — Keyboard.setStyle themes the KEYS, not
// that bar, so on the light/ivory theme it rendered as a black slab. We hide
// the native bar (initNativeKeyboard) and render this web replacement instead,
// which we fully control: it follows the in-app theme AND keeps the dismiss
// affordance the native bar provided.
//
// Positioning: Capacitor iOS defaults to resize:'native' — when the keyboard
// opens the WKWebView frame shrinks to the keyboard's top edge, so a
// position:fixed; bottom:0 element lands exactly above the keyboard. For
// resize:'none' (webview stays full-height behind the keyboard) we offset by
// the reported keyboard height instead. We read the mode once and the height
// from each willShow event, so it's correct under either configuration.
//
// iOS-only by design: that's the platform with the un-themeable bar. Android's
// system keyboard carries its own dismiss; web has no soft-keyboard chrome.

import { useState, useEffect } from 'react'
import { useTheme } from '../context/ThemeProvider.jsx'

function isIOSNative() {
  return typeof window !== 'undefined'
    && typeof window.Capacitor?.isNativePlatform === 'function'
    && window.Capacitor.isNativePlatform()
    && window.Capacitor?.getPlatform?.() === 'ios'
}

export default function KeyboardAccessoryBar() {
  const { theme } = useTheme()
  const [kb, setKb] = useState({ open: false, height: 0 })
  const [resizeMode, setResizeMode] = useState('native')

  useEffect(() => {
    if (!isIOSNative()) return
    const Keyboard = window.Capacitor?.Plugins?.Keyboard
    if (!Keyboard?.addListener) return

    // Read resize mode once — decides whether we offset by keyboard height.
    Keyboard.getResizeMode?.().then(r => { if (r?.mode) setResizeMode(r.mode) }).catch(() => {})

    const handles = []
    Promise.resolve(Keyboard.addListener('keyboardWillShow', (info) => {
      setKb({ open: true, height: info?.keyboardHeight || 0 })
    })).then(h => handles.push(h)).catch(() => {})
    Promise.resolve(Keyboard.addListener('keyboardWillHide', () => {
      setKb({ open: false, height: 0 })
    })).then(h => handles.push(h)).catch(() => {})
    return () => handles.forEach(h => { try { h?.remove?.() } catch { /* noop */ } })
  }, [])

  if (!kb.open) return null

  // native/body/ionic → webview already ends at the keyboard top (bottom:0).
  // none → webview is full-height, so lift the bar by the keyboard height.
  const bottom = resizeMode === 'none' ? kb.height : 0

  const isDark = theme?.isDark
  const barBg  = isDark ? '#241019' : '#EFE8DA'              // dark cellar vs warm ivory
  const border = isDark ? 'rgba(196,150,90,0.30)' : 'rgba(123,32,64,0.20)'
  const doneFg = isDark ? (theme?.colors?.gold || '#C4965A') : '#7B2040'  // gold on dark, burgundy on light

  const dismiss = () => {
    const el = document.activeElement
    if (el && typeof el.blur === 'function') el.blur()
    // Belt + suspenders — also ask the plugin to hide (covers contenteditable).
    window.Capacitor?.Plugins?.Keyboard?.hide?.().catch(() => {})
  }

  return (
    <div
      role="toolbar"
      aria-label="Keyboard controls"
      style={{
        position: 'fixed', left: 0, right: 0, bottom,
        height: 44,
        background: barBg,
        borderTop: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '0 14px',
        zIndex: 2147483000,   // above app content (the keyboard is a separate native layer)
        boxShadow: '0 -2px 12px rgba(0,0,0,0.14)',
      }}
    >
      <button
        type="button"
        // onPointerDown (not onClick) + preventDefault: fire BEFORE the focused
        // input blurs, so the tap still registers as the keyboard dismisses.
        onPointerDown={(e) => { e.preventDefault(); dismiss() }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: 'Raleway, sans-serif', fontSize: 16, fontWeight: 700,
          letterSpacing: '0.02em', color: doneFg, padding: '6px 10px',
          minWidth: 64, textAlign: 'right',
        }}
      >
        Done
      </button>
    </div>
  )
}
